import { useEffect, useState } from 'react';
import { useDialog } from '@ianesthesie-kit/redux';
import { AppDialogType } from '@/config';
import {
  PushNotificationService,
  useIsCommunicationEnabledOnHospital
} from '@/modules/notification';
import { useIsVerified } from '@/modules/userConfig';
import {
  AssignedQuestionnaire,
  QuestionnaireStatus,
  useAssignedQuestionnaires
} from '@/modules/questionnaire';
import { useAppSelector } from '@/hooks';
import { selectPushPermissionPostponed } from '@/modules/ui';
import { dialogsAllowedOnPage, selectIsAnyDialogOpen } from './util';

export const usePushNotificationChecker = (opts?: { skip?: boolean }) => {
  const { showDialog } = useDialog();
  const isVerified = useIsVerified();
  const [canShow, setCanShow] = useState(true);
  const [permission, setPermission] = useState<PermissionState>();
  const isAnyDialogOpen = useAppSelector(selectIsAnyDialogOpen);
  const isPostponed = useAppSelector(selectPushPermissionPostponed);
  const isNotificationsEnabled = useIsCommunicationEnabledOnHospital();

  const partialSkip =
    !!opts?.skip ||
    !isVerified ||
    !isNotificationsEnabled ||
    isAnyDialogOpen ||
    isPostponed ||
    permission !== 'prompt' ||
    !dialogsAllowedOnPage();
  const { data: isAnyQuestionnaireCompleted } = useAssignedQuestionnaires(
    { statuses: [QuestionnaireStatus.COMPLETED] },
    { skip: partialSkip, selectFromResult: selectIsResultEmpty }
  );

  const skip = partialSkip || !isAnyQuestionnaireCompleted;

  useEffect(() => {
    const t = setTimeout(() => {
      const push = PushNotificationService.getInstance();
      push
        .serviceWorkerReadyAndActive()
        .then(() => push.currentPermissionStateAdjusted())
        .then(setPermission);
    });
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const showDialogIfApplicable = async () => {
      if (skip || !canShow) {
        return;
      }

      // permission is guaranteed to be prompt at this point,
      // as if not, skip should be true

      const push = PushNotificationService.getInstance();
      const isDeviceSubscribed = !!(await push.currentDeviceSubscription());
      if (isDeviceSubscribed) {
        // if we reach this point, that means that this device is subscribed under another user
        // so we should delete the existing subscription and redo the process
        await push.unsubscribe();
      }

      showDialog({
        dialogType: AppDialogType.PUSH_NOTIFICATIONS,
        message: ''
      });

      setCanShow(false);
    };

    const t = setTimeout(showDialogIfApplicable, 2000);
    return () => clearTimeout(t);
  }, [canShow, showDialog, skip]);
};

function selectIsResultEmpty<T extends { data?: AssignedQuestionnaire[] }>({
  data,
  ...result
}: T) {
  return {
    ...result,
    data: !!data?.length
  };
}
